import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePortalSlug } from "@/contexts/PortalDomainContext";
import api from "@/lib/api";
import { loadTenantFont, resolveLogoUrl } from "@/lib/branding";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Recycle, MapPin, CheckCircle, Sparkle, ShieldCheck, UserCircle, Phone, Wind, Bug, ArrowRight, Star, Truck, Quotes } from "@phosphor-icons/react";
import PortalFooter from "@/components/PortalFooter";
import ServiceAreaMap from "@/components/ServiceAreaMap";

import ComingSoonView from "./sections/ComingSoonView";

export const TenantBrandingProvider = ({ children }) => children;

export default function PortalHome() {
  const slug = usePortalSlug();
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [err, setErr] = useState(null);
  const [refsTeaser, setRefsTeaser] = useState(null);

  useEffect(() => {
    api.get(`/tenants/public/${slug}`).then((r) => {
      setTenant(r.data);
      const b = r.data.branding || {};
      document.documentElement.style.setProperty("--tenant-primary", b.primary_color || "#2563EB");
      document.documentElement.style.setProperty("--tenant-secondary", b.secondary_color || "#0EA5E9");
      document.documentElement.style.setProperty("--tenant-font", `"${b.font_family || "Outfit"}", sans-serif`);
      loadTenantFont(b.font_family);
      document.title = r.data.meta_title || `${r.data.name} — Mülltonnenreinigung`;
    }).catch((e) => setErr(e.response?.data?.detail || "Mandant nicht gefunden"));
    // Referenzen-Teaser (non-blocking)
    api.get(`/portal/${slug}/references`).then((r) => setRefsTeaser(r.data)).catch(() => setRefsTeaser(null));
  }, [slug]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <h1 className="font-heading text-3xl font-bold mb-2">404</h1>
        <p className="text-zinc-500 mb-4">{err}</p>
        <Link to="/" className="underline text-sm">← Zur Tonneva Startseite</Link>
      </div>
    </div>
  );
  if (!tenant) return <div className="min-h-screen flex items-center justify-center">Lade…</div>;

  // Coming-Soon Ansicht wenn Mandant Service noch nicht freigegeben hat
  // (Preview-Modus: iframe zeigt immer ComingSoon, damit Editor Änderungen vor dem Save prüfen kann)
  const previewMode = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("_preview") === "1";
  if (tenant.service_status === "coming_soon" || previewMode) {
    return <ComingSoonView tenant={tenant} />;
  }

  const b = tenant.branding || {};
  return (
    <div className="min-h-screen bg-white font-tenant text-tenant-text" style={{ fontFamily: `var(--tenant-font)` }}>
      {/* Topbar Kicker */}
      <div className="text-white text-center text-xs md:text-sm tracking-wide py-3 md:py-4 px-4 font-semibold flex items-center justify-center gap-3" style={{ background: `linear-gradient(90deg, ${b.primary_color}, ${b.secondary_color || b.primary_color})` }} data-testid="portal-topbar">
        <Sparkle size={14} weight="fill" className="opacity-90 hidden sm:inline" />
        <span>Saubere Tonnen. Weniger Gerüche. Mehr Hygiene.</span>
        {tenant.phone && (
          <a href={`tel:${tenant.phone.replace(/\s/g,"")}`} className="hidden md:inline-flex items-center gap-1.5 ml-4 pl-4 border-l border-white/30 hover:opacity-80 transition" data-testid="topbar-phone">
            <Phone size={13} weight="bold" /> {tenant.phone}
          </a>
        )}
      </div>

      <header className="border-b border-black/5 sticky top-0 z-30 bg-white/95 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to={`/portal/${slug}`} className="flex items-center gap-3">
            {b.logo_url ? (
              <img src={resolveLogoUrl(b.logo_url)} alt={tenant.name} className="h-8" />
            ) : (
              <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: b.primary_color }}>
                <Recycle size={18} weight="bold" className="text-white" />
              </div>
            )}
            <div>
              <div className="font-bold tracking-tight">{tenant.name}</div>
              <div className="text-[10px] text-zinc-500">{tenant.city}</div>
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-1 text-xs text-zinc-600">
            <a href="#warum" className="px-3 py-2 rounded-md hover:bg-zinc-100 font-medium">Warum</a>
            <a href="#ablauf" className="px-3 py-2 rounded-md hover:bg-zinc-100 font-medium">Ablauf</a>
            <a href="#gebiete" className="px-3 py-2 rounded-md hover:bg-zinc-100 font-medium" data-testid="header-areas-link">Gebiete</a>
            <a href="#abos" className="px-3 py-2 rounded-md hover:bg-zinc-100 font-medium">Abos</a>
            <Link to={`/portal/${slug}/karte`} className="px-3 py-2 rounded-md hover:bg-zinc-100 font-medium" data-testid="header-map-link">Karte</Link>
            <Link to={`/portal/${slug}/referenzen`} className="px-3 py-2 rounded-md hover:bg-zinc-100 font-medium" data-testid="header-references-link">Referenzen</Link>
          </nav>
          <div className="flex items-center gap-2">
            {tenant.phone && (
              <a href={`tel:${tenant.phone.replace(/\s/g,"")}`} className="hidden md:inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md hover:bg-zinc-100" style={{ color: b.primary_color }} data-testid="header-phone">
                <Phone size={14} weight="bold" /> {tenant.phone}
              </a>
            )}
            <Link to={`/portal/${slug}/book`}>
              <Button className="rounded-md text-white" style={{ background: b.primary_color }} data-testid="header-book-btn">
                Jetzt buchen
              </Button>
            </Link>
            {user && user.role === "customer" && user.tenant_id === tenant.tenant_id ? (
              <Link to={`/portal/${slug}/account`} className="hidden sm:flex ml-2 items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md hover:bg-zinc-100" style={{ color: b.primary_color }} data-testid="header-account-link">
                <UserCircle size={16} weight="bold" /> Mein Konto
              </Link>
            ) : (
              <Link to={`/portal/${slug}/login`} className="hidden sm:flex ml-2 items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md hover:bg-zinc-100" style={{ color: b.primary_color }} data-testid="header-login-link">
                <UserCircle size={16} weight="bold" /> Anmelden
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="portal-hero">
        <div aria-hidden className="absolute inset-0 -z-10" style={{ background: `radial-gradient(ellipse at top left, ${b.primary_color}0F 0%, transparent 55%), radial-gradient(ellipse at bottom right, ${b.secondary_color || b.primary_color}12 0%, transparent 55%)` }} />
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold rounded-full" style={{ background: `${b.primary_color}15`, color: b.primary_color }}>
              <Sparkle size={11} weight="fill" /> Professionelle Reinigung direkt vor Ort
            </div>
            <h1 className="font-heading text-5xl md:text-6xl font-extrabold tracking-tight mt-5 leading-[1.05]" data-testid="hero-headline">
              {tenant.name.split(" ")[0]}
              <br />
              <span style={{ color: b.primary_color }}>{tenant.name.split(" ").slice(1).join(" ") || "Tonnenreinigung"}</span>
            </h1>
            <p className="mt-5 text-lg text-zinc-600 max-w-lg leading-relaxed">
              {b.tagline || `Wir reinigen Ihre Mülltonne gründlich, hygienisch und zuverlässig — gegen unangenehme Gerüche, hartnäckige Rückstände, Bakterien und Maden.`}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={`/portal/${slug}/book`}>
                <Button className="rounded-md text-white h-12 px-7 text-base" style={{ background: b.primary_color }} data-testid="hero-book-btn">
                  Tonnenreinigung buchen <ArrowRight size={14} weight="bold" className="ml-1" />
                </Button>
              </Link>
              <a href="#ablauf">
                <Button variant="outline" className="rounded-md h-12 px-7 text-base" data-testid="hero-how-btn">So funktioniert&apos;s</Button>
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-zinc-500">
              <div className="flex items-center gap-1.5"><CheckCircle size={14} weight="fill" style={{ color: b.primary_color }} /> Hygienisch sauber</div>
              <div className="flex items-center gap-1.5"><CheckCircle size={14} weight="fill" style={{ color: b.primary_color }} /> Umweltfreundlich</div>
              <div className="flex items-center gap-1.5"><CheckCircle size={14} weight="fill" style={{ color: b.primary_color }} /> Direkt vor Ort</div>
            </div>
            {tenant.service_area_zips && tenant.service_area_zips.length > 0 && (
              <div className="mt-6 inline-flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg bg-zinc-50 border border-black/5 text-xs" data-testid="service-area-info">
                <MapPin size={12} weight="bold" style={{ color: b.primary_color }} />
                <span className="text-zinc-500 uppercase tracking-widest text-[9px] font-bold">Wir bedienen</span>
                <div className="flex flex-wrap gap-1">
                  {tenant.service_area_zips.slice(0, 6).map((z) => (
                    <span key={z} className="font-mono px-1.5 py-0.5 bg-white rounded text-zinc-700 border border-black/5" data-testid={`service-zip-${z}`}>{z}</span>
                  ))}
                  {tenant.service_area_zips.length > 6 && <span className="text-zinc-400">+{tenant.service_area_zips.length - 6} weitere</span>}
                </div>
              </div>
            )}
          </div>

          {/* Visual Card — Logo/Fahrzeugbild-Platzhalter mit Floating Badge */}
          <div className="relative animate-fade-up" style={{ animationDelay: "150ms" }}>
            <div className="rounded-3xl overflow-hidden aspect-[4/5] md:aspect-[4/3] relative shadow-2xl" style={{ background: `linear-gradient(135deg, ${b.primary_color}, ${b.secondary_color || b.primary_color})` }} data-testid="hero-visual">
              {b.hero_image_url || b.logo_url ? (
                <img src={resolveLogoUrl(b.hero_image_url || b.logo_url)} alt={tenant.name} className="absolute inset-0 w-full h-full object-cover object-center" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center p-8">
                  <div className="text-center text-white">
                    <div className="w-24 h-24 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center mx-auto mb-5">
                      <Truck size={46} weight="regular" />
                    </div>
                    <div className="font-heading font-bold text-2xl mb-2">Logo oder Fahrzeugbild</div>
                    <p className="text-sm opacity-90 max-w-xs mx-auto">Hier kann später Ihr Logo oder ein starkes Foto des Reinigungsfahrzeugs eingesetzt werden.</p>
                  </div>
                </div>
              )}
            </div>
            <div className="absolute -bottom-4 -left-4 md:-left-6 bg-white shadow-xl rounded-full px-5 py-3 flex items-center gap-2 border border-black/5">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white" style={{ background: b.primary_color }}>
                <CheckCircle size={16} weight="fill" />
              </div>
              <div className="font-semibold text-sm">Professionelle Spezialreinigung</div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits — Warum reinigen lassen? */}
      <section className="border-t border-black/5 py-20 bg-zinc-50" id="warum" data-testid="portal-benefits">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold rounded-full mb-3" style={{ background: `${b.primary_color}15`, color: b.primary_color }}>
              Mehr als nur sauber
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Warum sollten Sie Ihre Mülltonne reinigen lassen?</h2>
            <p className="text-sm text-zinc-600 mt-3">Nach der Leerung bleiben oft Flüssigkeiten, Essensreste und Schmutz zurück. Genau dort entstehen Gerüche, Keime und Ungeziefer.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: <Wind size={22} weight="duotone" />, title: "Weniger Gerüche", desc: "Rückstände und Verschmutzungen werden gründlich entfernt, damit unangenehme Gerüche deutlich reduziert werden." },
              { icon: <ShieldCheck size={22} weight="duotone" />, title: "Mehr Hygiene", desc: "Eine regelmäßige Reinigung hilft dabei, Bakterien, Keime und hartnäckige Ablagerungen zu reduzieren." },
              { icon: <Bug size={22} weight="duotone" />, title: "Weniger Ungeziefer", desc: "Saubere Tonnen bieten Fliegen, Maden und anderem Ungeziefer deutlich weniger Nährboden." },
            ].map((item, i) => (
              <article key={item.title} className="bg-white rounded-2xl p-7 border border-black/5 hover:shadow-lg transition-shadow" data-testid={`benefit-card-${i}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white mb-5" style={{ background: `linear-gradient(135deg, ${b.primary_color}, ${b.secondary_color || b.primary_color})` }}>
                  {item.icon}
                </div>
                <h3 className="font-heading font-bold text-lg tracking-tight mb-2">{item.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Ablauf — So einfach geht's */}
      <section className="border-t border-black/5 py-20 bg-white" id="ablauf" data-testid="portal-steps">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold rounded-full mb-3" style={{ background: `${b.primary_color}15`, color: b.primary_color }}>
              Einfach und unkompliziert
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">So einfach geht&apos;s</h2>
            <p className="text-sm text-zinc-600 mt-3">In wenigen Schritten zur hygienisch sauberen Mülltonne.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-5">
            {[
              { title: "Abo auswählen", desc: "Wählen Sie die passende Tonnengröße und den gewünschten Reinigungsrhythmus." },
              { title: "Termin erhalten", desc: "Sie erhalten Ihren Reinigungstermin passend zur regulären Tonnenleerung." },
              { title: "Tonne stehen lassen", desc: "Lassen Sie Ihre geleerte Tonne einfach am vereinbarten Platz stehen." },
              { title: "Wir reinigen", desc: "Unser Spezialfahrzeug übernimmt die gründliche Reinigung direkt vor Ort." },
            ].map((s, i) => (
              <div key={s.title} className="relative bg-white border border-black/5 rounded-2xl p-6 hover:border-black/20 transition" data-testid={`step-card-${i}`}>
                <div className="font-heading text-5xl font-black opacity-15 leading-none mb-3" style={{ color: b.primary_color }}>0{i + 1}</div>
                <h3 className="font-bold text-base tracking-tight mb-1.5">{s.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Servicegebiete — Wo wir aktiv sind */}
      {(() => {
        const areas = (tenant.service_areas && tenant.service_areas.length > 0)
          ? tenant.service_areas
          : (tenant.service_area_zips || []).map((z) => ({ zip: z, city: "" }));
        if (!areas.length) return null;
        // Group by city (fallback = "Weitere PLZs")
        const grouped = {};
        areas.forEach((a) => {
          const key = a.city || "__no_city__";
          if (!grouped[key]) grouped[key] = { city: a.city || "", zips: [] };
          if (a.zip && !grouped[key].zips.includes(a.zip)) grouped[key].zips.push(a.zip);
        });
        const groups = Object.values(grouped).sort((a, x) => (a.city || "zzz").localeCompare(x.city || "zzz"));
        return (
          <section className="border-t border-black/5 py-20 bg-white" id="gebiete" data-testid="portal-service-areas">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-14 max-w-2xl mx-auto">
                <div className="inline-flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold rounded-full mb-3" style={{ background: `${b.primary_color}15`, color: b.primary_color }}>
                  <MapPin size={11} weight="fill" /> Unsere Einsatzgebiete
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Wir sind in Ihrer Nähe</h2>
                <p className="text-sm text-zinc-600 mt-3">
                  Diese Orte und PLZ-Gebiete bedienen wir regelmäßig. Ihre Adresse dabei? Direkt buchen. Nicht dabei? Als Interessent eintragen — bei genügend Anfragen kommen wir vorbei.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {groups.map((g, i) => (
                  <article
                    key={`${g.city || "no"}-${i}`}
                    className="group relative bg-white border border-black/10 rounded-2xl p-5 hover:border-transparent hover:shadow-xl transition-all"
                    data-testid={`service-area-card-${i}`}
                  >
                    <div aria-hidden className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition -z-0" style={{ background: `linear-gradient(135deg, ${b.primary_color}08, ${b.secondary_color || b.primary_color}08)` }} />
                    <div className="relative flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0" style={{ background: `linear-gradient(135deg, ${b.primary_color}, ${b.secondary_color || b.primary_color})` }}>
                        <MapPin size={18} weight="fill" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-bold text-base tracking-tight leading-tight">
                          {g.city || "Weitere PLZs"}
                        </h3>
                        {g.zips.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {g.zips.map((z) => (
                              <span key={z} className="font-mono text-[11px] px-1.5 py-0.5 bg-zinc-100 text-zinc-700 rounded" data-testid={`area-zip-${z}`}>
                                {z}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* Mapbox-Karte */}
              <div className="mt-10">
                <ServiceAreaMap slug={slug} primaryColor={b.primary_color} secondaryColor={b.secondary_color || b.primary_color} />
              </div>

              <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                <Link to={`/portal/${slug}/book`}>
                  <Button className="rounded-md text-white h-11 px-6 text-sm" style={{ background: b.primary_color }} data-testid="areas-book-btn">
                    Jetzt Adresse prüfen & buchen <ArrowRight size={13} weight="bold" className="ml-1" />
                  </Button>
                </Link>
                <Link to={`/portal/${slug}/interest-map`}>
                  <Button variant="outline" className="rounded-md h-11 px-6 text-sm" data-testid="areas-interest-btn">
                    Nicht dabei? Interessenten-Karte ansehen
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Pricing — Unsere Abos */}
      <section className="border-t border-black/5 py-20 bg-zinc-50" id="abos" data-testid="portal-pricing">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold rounded-full mb-3" style={{ background: `${b.primary_color}15`, color: b.primary_color }}>
              Passend für jede Tonne
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Unsere Abos für Ihre Mülltonne</h2>
            <p className="text-sm text-zinc-600 mt-3">Faire Preise, jederzeit anpassbar oder kündbar.</p>
          </div>
          {tenant.plans && tenant.plans.filter((p) => p.active !== false).length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {tenant.plans.filter((p) => p.active !== false).map((p, idx) => {
                const isFeatured = idx === 1;
                return (
                  <article key={p.plan_id} className={`relative rounded-2xl p-8 border transition hover:-translate-y-1 ${isFeatured ? "border-transparent shadow-2xl" : "border-black/10 bg-white shadow-sm hover:shadow-md"}`} style={isFeatured ? { background: `linear-gradient(135deg, ${b.primary_color}, ${b.secondary_color || b.primary_color})`, color: "white" } : {}} data-testid={`portal-plan-${idx}`}>
                    {isFeatured && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white text-xs font-bold px-3 py-1 rounded-full shadow-md tracking-wide" style={{ color: b.primary_color }}>
                        ⭐ Beliebtestes Abo
                      </div>
                    )}
                    <h3 className={`font-heading font-bold text-xl tracking-tight mb-3 ${isFeatured ? "" : ""}`}>{p.name}</h3>
                    <div className="flex items-baseline gap-1">
                      <span className="font-heading text-4xl font-black tracking-tight">€{(p.price_per_cleaning_cents/100).toFixed(2)}</span>
                      <span className={`text-sm ${isFeatured ? "opacity-80" : "text-zinc-400"}`}>/ Reinigung</span>
                    </div>
                    {p.description && <p className={`text-sm mt-3 leading-relaxed ${isFeatured ? "opacity-90" : "text-zinc-600"}`}>{p.description}</p>}
                    <ul className={`mt-5 space-y-2 text-sm ${isFeatured ? "opacity-95" : "text-zinc-700"}`}>
                      <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className={isFeatured ? "text-white" : ""} style={isFeatured ? {} : { color: b.primary_color }} /> {p.cleanings_per_year} Reinigungen pro Jahr</li>
                      <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className={isFeatured ? "text-white" : ""} style={isFeatured ? {} : { color: b.primary_color }} /> Reinigung direkt vor Ort</li>
                      <li className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className={isFeatured ? "text-white" : ""} style={isFeatured ? {} : { color: b.primary_color }} /> Jederzeit pausierbar</li>
                    </ul>
                    <Link to={`/portal/${slug}/book?plan=${p.plan_id}`}>
                      <Button className="mt-6 w-full rounded-md h-11" style={isFeatured ? { background: "white", color: b.primary_color } : { background: b.primary_color, color: "white" }} data-testid={`portal-plan-cta-${idx}`}>
                        Jetzt auswählen
                      </Button>
                    </Link>
                  </article>
                );
              })}
            </div>
          ) : (
            /* Fallback demo prices if no plans configured yet */
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                { name: "Basis", price: 9.90, blurb: "Für kleinere Tonnen und gelegentliche Reinigung.", bullets: ["Bis 120 Liter", "Reinigung direkt vor Ort", "Geruchs- und Schmutzreduktion"], cta: "Jetzt auswählen" },
                { name: "Komfort", price: 14.90, blurb: "Die praktische Lösung für regelmäßige Sauberkeit.", bullets: ["Bis 240 Liter", "Regelmäßiger Reinigungsrhythmus", "Ideal für Privathaushalte"], cta: "Jetzt auswählen", featured: true },
                { name: "Großtonne", price: 24.90, blurb: "Für große Tonnen, Mehrfamilienhäuser und Gewerbe.", bullets: ["Bis 1.100 Liter", "Individuelle Intervalle", "Für Gewerbe und Wohnanlagen"], cta: "Angebot anfragen" },
              ].map((p, idx) => (
                <article key={p.name} className={`relative rounded-2xl p-8 border transition hover:-translate-y-1 ${p.featured ? "border-transparent shadow-2xl" : "border-black/10 bg-white shadow-sm hover:shadow-md"}`} style={p.featured ? { background: `linear-gradient(135deg, ${b.primary_color}, ${b.secondary_color || b.primary_color})`, color: "white" } : {}} data-testid={`portal-plan-fallback-${idx}`}>
                  {p.featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white text-xs font-bold px-3 py-1 rounded-full shadow-md tracking-wide" style={{ color: b.primary_color }}>
                      ⭐ Beliebtestes Abo
                    </div>
                  )}
                  <h3 className="font-heading font-bold text-xl tracking-tight mb-3">{p.name}</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="font-heading text-4xl font-black tracking-tight">€{p.price.toFixed(2)}</span>
                    <span className={`text-sm ${p.featured ? "opacity-80" : "text-zinc-400"}`}>/ Reinigung</span>
                  </div>
                  <p className={`text-sm mt-3 leading-relaxed ${p.featured ? "opacity-90" : "text-zinc-600"}`}>{p.blurb}</p>
                  <ul className={`mt-5 space-y-2 text-sm ${p.featured ? "opacity-95" : "text-zinc-700"}`}>
                    {p.bullets.map((bl) => (
                      <li key={bl} className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className={p.featured ? "text-white" : ""} style={p.featured ? {} : { color: b.primary_color }} /> {bl}</li>
                    ))}
                  </ul>
                  <Link to={`/portal/${slug}/book`}>
                    <Button className="mt-6 w-full rounded-md h-11" style={p.featured ? { background: "white", color: b.primary_color } : { background: b.primary_color, color: "white" }}>
                      {p.cta}
                    </Button>
                  </Link>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Referenzen-Teaser (nur wenn Reviews vorhanden) */}
      {refsTeaser && refsTeaser.counts?.total > 0 && (
        <section className="border-t border-black/5 py-16 bg-white" data-testid="portal-references-teaser">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold rounded-full mb-3" style={{ background: `${b.primary_color}15`, color: b.primary_color }}>
                  <Star size={11} weight="fill" /> Was Kunden sagen
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Vertrauen aus der Region</h2>
                {refsTeaser.average_rating && (
                  <div className="mt-3 flex items-center gap-2">
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map((n) => (
                        <Star key={n} size={16} weight="fill" className={n <= Math.round(refsTeaser.average_rating) ? "text-amber-400" : "text-zinc-200"} />
                      ))}
                    </div>
                    <span className="text-sm font-semibold">{refsTeaser.average_rating.toFixed(1)}</span>
                    <span className="text-xs text-zinc-500">· {refsTeaser.counts.testimonials + refsTeaser.counts.google_reviews} Bewertungen</span>
                  </div>
                )}
              </div>
              <Link to={`/portal/${slug}/referenzen`} className="inline-flex items-center gap-1 text-sm font-semibold hover:underline" style={{ color: b.primary_color }} data-testid="portal-references-link">
                Alle Referenzen ansehen <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {[...(refsTeaser.google_reviews || []), ...(refsTeaser.testimonials || [])].slice(0, 3).map((r) => (
                <article key={r.reference_id} className="bg-zinc-50 border border-black/5 rounded-xl p-5" data-testid={`teaser-review-${r.reference_id}`}>
                  <Quotes size={20} weight="fill" style={{ color: `${b.primary_color}60` }} />
                  <div className="flex gap-0.5 mt-2 mb-2">
                    {[1,2,3,4,5].map((n) => (
                      <Star key={n} size={12} weight={n <= (r.rating || 0) ? "fill" : "regular"} className={n <= (r.rating || 0) ? "text-amber-400" : "text-zinc-200"} />
                    ))}
                  </div>
                  <p className="text-sm text-zinc-700 leading-relaxed line-clamp-4">&ldquo;{r.text}&rdquo;</p>
                  <div className="mt-3 text-xs text-zinc-500">
                    <strong className="text-zinc-800">{r.author_name}</strong>
                    {r.author_location && <span> · {r.author_location}</span>}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Kontakt CTA-Box */}
      <section className="border-t border-black/5 py-20" id="kontakt" data-testid="portal-cta">
        <div className="max-w-5xl mx-auto px-6">
          <div className="rounded-3xl p-8 md:p-12 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${b.primary_color} 0%, ${b.secondary_color || b.primary_color} 100%)` }}>
            <div aria-hidden className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
            <div aria-hidden className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Jetzt saubere Mülltonnen sichern</h2>
                <p className="mt-3 opacity-90 max-w-lg">Wählen Sie Ihr passendes Modell und buchen Sie Ihre professionelle Tonnenreinigung.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                <Link to={`/portal/${slug}/book`}>
                  <Button className="rounded-md h-12 px-7 bg-white text-black hover:bg-zinc-100 w-full sm:w-auto" data-testid="cta-book-btn">
                    Jetzt buchen <ArrowRight size={14} weight="bold" className="ml-1" />
                  </Button>
                </Link>
                {tenant.email ? (
                  <a href={`mailto:${tenant.email}`}>
                    <Button variant="outline" className="rounded-md h-12 px-7 border-white text-white hover:bg-white/10 bg-transparent w-full sm:w-auto" data-testid="cta-contact-btn">
                      Kontakt aufnehmen
                    </Button>
                  </a>
                ) : tenant.phone ? (
                  <a href={`tel:${tenant.phone.replace(/\s/g,"")}`}>
                    <Button variant="outline" className="rounded-md h-12 px-7 border-white text-white hover:bg-white/10 bg-transparent w-full sm:w-auto" data-testid="cta-contact-btn">
                      <Phone size={14} weight="bold" className="mr-1.5" /> {tenant.phone}
                    </Button>
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      <PortalFooter tenant={tenant} slug={slug} primary={b.primary_color} />
    </div>
  );
}

// ==============================================================
// COMING-SOON VIEW mit Newsletter-Anmeldung
// ==============================================================
